import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Volume2, Pause, Loader2, VolumeX, Settings } from 'lucide-react';
import './ReelListenButton.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Deepgram Aura-2 voices surfaced to the visitor (matches the Compass
// TTS player + backend PUBLIC_VOICES).
const VOICES = {
  andromeda: 'Andromeda',
  luna: 'Luna',
  asteria: 'Asteria',
  athena: 'Athena',
  orion: 'Orion',
  arcas: 'Arcas',
};
const ALLOWED = Object.keys(VOICES);

// Shares the narration-voice preference with the Compass guides.
const readVoice = () => {
  try {
    const saved = localStorage.getItem('cadence_voice');
    return ALLOWED.includes(saved) ? saved : 'arcas';
  } catch {
    return 'arcas';
  }
};

/**
 * Compact "Listen" pill + settings popover for the cinematic reel
 * (Our Story chapters / How It Works stages). Fetches a Deepgram MP3 for
 * the supplied text from the shared backend and plays it with a native
 * <audio> element. Supports:
 *   - autoStart: begin playback automatically on mount (hands-free autoplay)
 *   - onEnded:   notify parent when narration finishes (to auto-advance)
 *   - voice picker + auto-play toggle in a gear popover
 */
const ReelListenButton = ({
  text,
  autoStart = false,
  onStart,
  onEnded,
  autoplayEnabled = true,
  onToggleAutoplay,
  label = 'Listen to this chapter',
  autoplayLabel = 'Auto-play chapters',
  testId = 'reel-listen-btn',
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [voice, setVoice] = useState(readVoice);
  const [showSettings, setShowSettings] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem('cadence_voice', voice);
    } catch {
      /* ignore */
    }
  }, [voice]);

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const fetchAndPlay = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice, speed: 1.0 }),
      });
      if (!res.ok) throw new Error('tts');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = url;
      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [text, voice]);

  const play = useCallback(async () => {
    if (urlRef.current && audioRef.current) {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch {
        /* ignore */
      }
      return;
    }
    await fetchAndPlay();
  }, [fetchAndPlay]);

  // Hands-free autoplay: when the parent advances the reel during a
  // narration session, the freshly-mounted button starts on its own.
  useEffect(() => {
    if (autoStart && !startedRef.current) {
      startedRef.current = true;
      if (onStart) onStart();
      fetchAndPlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const toggle = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (!startedRef.current) {
        startedRef.current = true;
        if (onStart) onStart();
      }
      play();
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (onEnded) onEnded();
  };

  const changeVoice = (key) => {
    setVoice(key);
    // Drop the cached clip so the next press regenerates in the new voice.
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
  };

  return (
    <div className="reel-listen">
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        onError={() => {
          setError(true);
          setIsPlaying(false);
        }}
      />
      <div className="reel-listen-controls">
        <button
          type="button"
          className={`reel-listen-btn ${isPlaying ? 'is-playing' : ''}`}
          onClick={toggle}
          disabled={isLoading}
          data-testid={testId}
          aria-label={isPlaying ? 'Pause narration' : label}
        >
          {isLoading ? (
            <Loader2 size={16} className="reel-listen-spin" />
          ) : error ? (
            <VolumeX size={16} />
          ) : isPlaying ? (
            <Pause size={16} />
          ) : (
            <Volume2 size={16} />
          )}
          <span className="reel-listen-label">
            {isLoading
              ? 'Loading\u2026'
              : error
              ? 'Audio unavailable'
              : isPlaying
              ? 'Pause'
              : label}
          </span>
        </button>

        <button
          type="button"
          className={`reel-listen-gear ${showSettings ? 'is-open' : ''}`}
          onClick={() => setShowSettings((v) => !v)}
          aria-label="Narration settings"
          aria-expanded={showSettings}
          data-testid="reel-listen-settings-btn"
        >
          <Settings size={15} />
        </button>
      </div>

      {showSettings && (
        <div className="reel-listen-popover" data-testid="reel-listen-settings">
          <p className="reel-listen-pop-label">Narration voice</p>
          <div className="reel-listen-voices">
            {Object.entries(VOICES).map(([key, name]) => (
              <button
                key={key}
                type="button"
                className={`reel-listen-voice ${voice === key ? 'is-selected' : ''}`}
                onClick={() => changeVoice(key)}
                data-testid={`reel-listen-voice-${key}`}
              >
                {name}
                {voice === key && <span className="reel-listen-check">✓</span>}
              </button>
            ))}
          </div>
          <label className="reel-listen-autoplay">
            <input
              type="checkbox"
              checked={autoplayEnabled}
              onChange={(e) => onToggleAutoplay && onToggleAutoplay(e.target.checked)}
              data-testid="reel-listen-autoplay-toggle"
            />
            <span>{autoplayLabel}</span>
          </label>
        </div>
      )}
    </div>
  );
};

export default ReelListenButton;
